import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBagIcon, CalendarIcon, StarIcon, SparklesIcon, MessageSquareIcon, ArrowRightIcon, BookOpenIcon, BoxIcon } from 'lucide-react';
import CustomPromotionalBannerCarousel from '../components/UI/CustomPromotionalBannerCarousel';
import UnifiedBookCard from '../components/Books/UnifiedBookCard';
import GraphicsCarousel from '../components/Graphics/GraphicsCarousel';
import StoryStackHero from '../components/Home/StoryStackHero';
import DiscoveryRow from '../components/Home/DiscoveryRow';
import { FeaturedMerchCard } from '../components/Home/FeaturedMerchCard';
import { FeaturedBookBoxCard } from '../components/Home/FeaturedBookBoxCard';
import { EventTeaserCard } from '../components/Home/EventTeaserCard';
import { TroveArticleCard } from '../components/Home/TroveArticleCard';
import { NewsletterSection } from '../components/Home/NewsletterSection';
import { VendorCTASection } from '../components/Home/VendorCTASection';
import { ScrollableRow } from '../components/UI/ScrollableRow';
import AuthorSpotlightCarousel from '../components/Home/AuthorSpotlightCarousel';
import { samplePromotionalGraphics } from '../data/samplePromotionalGraphics';
import { allBooks } from '../data/booksData';
import { defaultDiscoveryRows, aftertideDiscoveryRows, getBooksForDiscoveryRow } from '../data/discoveryRowsData';
import { featuredMerch, troveArticles, spotlightAuthors, readerReviews, upcomingEvents, featuredBookBoxes } from '../data/homePageData';
import { aftertideFeaturedMerch, aftertideTroveArticles, aftertideSpotlightAuthors, aftertideReaderReviews, aftertideUpcomingEvents, aftertideFeaturedBookBoxes } from '../data/aftertideHomePageData';
import { usePlatform } from '../context/PlatformContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import { handleImgError } from '../utils/imageUtils';
const Home = () => {
  const {
    isAfterTide
  } = usePlatform();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]);
  // Platform-aware data selection
  const activeFeaturedMerch = isAfterTide ? aftertideFeaturedMerch : featuredMerch;
  const activeTroveArticles = isAfterTide ? aftertideTroveArticles : troveArticles;
  const activeSpotlightAuthors = isAfterTide ? aftertideSpotlightAuthors : spotlightAuthors;
  const activeReaderReviews = isAfterTide ? aftertideReaderReviews : readerReviews;
  const activeUpcomingEvents = isAfterTide ? aftertideUpcomingEvents : upcomingEvents;
  const activeDiscoveryRows = isAfterTide ? aftertideDiscoveryRows : defaultDiscoveryRows;
  const activeBookBoxes = isAfterTide ? aftertideFeaturedBookBoxes : featuredBookBoxes;
  // Platform-aware copy
  const platformTagline = isAfterTide ? 'AfterTide: Boundary-Pushing Fiction' : 'LitLaunch: Where Indie Books Belong';
  const platformSubtitle = isAfterTide ? 'Navigate dark fiction with confidence using our transparent classification system.' : 'Discover books by tropes, story themes, spice levels, and more.';
  // AfterTide-specific accent colors for inline elements
  const iconAccent = isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500';
  const iconAccentAmber = isAfterTide ? 'text-[#e2b04a]' : 'text-amber-500';
  const featuredGraphics = samplePromotionalGraphics.slice(0, 8);
  // Generate dynamic personalized row based on selected moods
  const personalizedBooks = selectedMoods.length > 0 ? getBooksForDiscoveryRow(allBooks, {
    tags: selectedMoods.map(m => m.toLowerCase().replace(/\s+/g, '-'))
  }) : [];
  return <div className={`w-full ${dk.page}`} data-id="element-10033" data-mp="qnsq7ad5pk9">
      {/* Banner Carousel */}
      <CustomPromotionalBannerCarousel data-id="element-10034" data-mp="1h18a25gbto1c" />

      {/* Tagline + Story Stack Hero (combined compact section) */}
      <section className={`pt-8 pb-4 sm:pt-10 sm:pb-6 ${dk.page}`} data-id="element-10035" data-mp="1c3gm1misym2f">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10036" data-mp="27ey75p657luw">
          <div className="text-center mb-6 sm:mb-8" data-id="element-10037" data-mp="2ccpzulem3c05">
            <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold mb-1.5 sm:mb-2 ${isAfterTide ? 'text-[#E2B04A]' : 'text-[#7E8CE0]'}`} data-id="element-10038" data-mp="2cdhtvrsk91ob">
              {platformTagline}
            </h2>
            <p className={`text-sm sm:text-base ${dk.textSecondary}`} data-id="element-10039" data-mp="2m92t5i68vnud">
              {platformSubtitle}
            </p>
          </div>
          <StoryStackHero onMoodSelect={setSelectedMoods} data-id="element-10040" data-mp="2vbc1c1e1j54l" />
        </div>
      </section>

      {/* Dynamic Personalized Row (shows only if moods are selected) */}
      {selectedMoods.length > 0 && personalizedBooks.length > 0 && <DiscoveryRow row={{
      id: 'personalized-stack',
      title: 'Based on Your Story Stack',
      description: `Books matching: ${selectedMoods.join(', ')}`,
      emoji: '🎯',
      type: 'personalized',
      filters: {},
      accentColor: isAfterTide ? 'text-[#e2b04a]' : 'text-indigo-500'
    }} books={personalizedBooks} data-id="element-10041" data-mp="350c3turr4inj" />}

      {/* Metadata-Driven Discovery Feed */}
      {activeDiscoveryRows.map((row, index) => {
      const books = getBooksForDiscoveryRow(allBooks, row.filters);
      // Alternate background colors for visual rhythm
      const bgClass = index % 2 === 0 ? dk.surface : dk.page;
      return <div key={row.id} className={bgClass} data-id="element-10042" data-mp="1m51xw0g53r86" data-mpi={index}>
            <DiscoveryRow row={row} books={books} data-id="element-10043" data-mp="1h3mesdxivusf" />
          </div>;
    })}

      {/* Book Graphics */}
      <section className={`py-12 ${dk.page}`} data-id="element-10044" data-mp="1cc4fdub5e29k">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10045" data-mp="1catcdwpj0yji">
          <div className="flex items-center justify-between mb-6" data-id="element-10046" data-mp="17djmkfy86kv5">
            <div data-id="element-10047" data-mp="24tqfodjbtgfq">
              <h2 className={`text-2xl font-bold flex items-center ${dk.textHeading}`} data-id="element-10048" data-mp="24u8kvznyv4zs">
                <BookOpenIcon className={`h-6 w-6 mr-2 ${iconAccent}`} data-id="element-10049" data-mp="201fivjjvuare" />
                Book Graphics
              </h2>
              <p className={dk.textSecondary} data-id="element-10050" data-mp="3ucyi6m1ki6ki">
                Explore author-created visuals highlighting tropes, spice
                levels, and book features
              </p>
            </div>
            <Link to="/books/graphics" className={`font-medium flex items-center ${rt.accentLink}`} data-id="element-10051" data-mp="3kiydgla773cb">
              View all
              <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-10052" data-mp="3al1vb9iuf84p" />
            </Link>
          </div>
          <GraphicsCarousel graphics={featuredGraphics} aspectRatio="portrait" showOverlay={true} data-id="element-10053" data-mp="3fmheew1gn4kg" />
        </div>
      </section>

      {/* Author Spotlight */}
      <section className={`py-12 ${dk.surface}`} data-id="element-10054" data-mp="36ljlp9wh2jh6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10055" data-mp="zutkzalnnu6e">
          <div className="flex items-center justify-between mb-6" data-id="element-10056" data-mp="zsh5d43ddvwi">
            <h2 className={`text-2xl font-bold flex items-center ${dk.textHeading}`} data-id="element-10057" data-mp="14slkapomqikm">
              <SparklesIcon className={`h-6 w-6 mr-2 ${iconAccentAmber}`} data-id="element-10058" data-mp="1ey3crryrwu89" />
              Author Spotlight
            </h2>
            <Link to="/authors" className={`font-medium flex items-center ${rt.accentLink}`} data-id="element-10059" data-mp="19we42iz0wsnc">
              View all
              <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-10060" data-mp="1iy40y4bocjsi" />
            </Link>
          </div>
          <AuthorSpotlightCarousel authors={activeSpotlightAuthors} data-id="element-10061" data-mp="1xn9y7mqybzxr" />
        </div>
      </section>

      {/* Reader Reviews */}
      <section className={`py-12 ${dk.page}`} data-id="element-10062" data-mp="1sogp06ip6v3e">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10063" data-mp="26ohlzavudidl">
          <h2 className={`text-2xl font-bold mb-8 flex items-center ${dk.textHeading}`} data-id="element-10064" data-mp="26mnasoadh4mp">
            <MessageSquareIcon className={`h-6 w-6 mr-2 ${iconAccent}`} data-id="element-10065" data-mp="22lbg44cn5t7n" />
            Recent Reader Reviews
          </h2>
          <ScrollableRow data-id="element-10066" data-mp="89n1626etqn9">
            {activeReaderReviews.map(review => <div key={review.id} className="flex-shrink-0 w-[280px] sm:w-[300px] snap-start" data-id="element-10067" data-mp="wu342q4gim6a">
                <div className={`rounded-xl p-6 shadow-sm border flex flex-col h-full ${dk.card}`} data-id="element-10068" data-mp="11s4njc5sonia">
                  <div className="flex items-start gap-4 mb-4" data-id="element-10069" data-mp="16lzd2w82zwpa">
                    <img src={review.bookCover} alt={review.bookTitle} className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0" loading="lazy" onError={handleImgError} data-id="element-10070" data-mp="1ak6jax4x5fm8" />
                    <div data-id="element-10071" data-mp="muc80k08zz43">
                      <div className="flex text-yellow-400 mb-1" data-id="element-10072" data-mp="mxzzzeq1odgp">
                        {[...Array(5)].map((_, i) => <StarIcon key={i} className={`h-3.5 w-3.5 ${i < review.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} data-id="element-10073" data-mp="1ybave77tuiep" data-mpi={i} />)}
                      </div>
                      <h3 className={`font-bold text-sm line-clamp-2 ${dk.textPrimary}`} data-id="element-10074" data-mp="2dfwtxr9lmbog">
                        {review.bookTitle}
                      </h3>
                      <p className={`text-xs ${dk.textSecondary}`} data-id="element-10075" data-mp="1zmnqe78c0kdu">
                        by {review.author}
                      </p>
                    </div>
                  </div>
                  <blockquote className={`flex-1 text-sm italic mb-4 ${dk.textSecondary}`} data-id="element-10076" data-mp="1ulhygbmhx15d">
                    "{review.quote}"
                  </blockquote>
                  <div className="mt-auto flex items-center justify-between" data-id="element-10077" data-mp="1krhljnkz2rm3">
                    <div className="flex flex-col" data-id="element-10078" data-mp="1gpe84teqtcy6">
                      <span className={`text-xs font-medium ${dk.textPrimary}`} data-id="element-10079" data-mp="3f3xyf789x3zx">
                        {review.reviewerName}
                      </span>
                      <span className={`text-[10px] ${dk.textMuted}`} data-id="element-10080" data-mp="3b2m78x9r4apm">
                        {review.date}
                      </span>
                    </div>
                    <Link to={`/book/${review.bookId}`} className={`text-xs font-medium ${rt.accentLink}`} data-id="element-10081" data-mp="8lycm9y3faex">
                      View Book →
                    </Link>
                  </div>
                </div>
              </div>)}
          </ScrollableRow>
        </div>
      </section>

      {/* Merch Preview */}
      <section className={`py-12 ${dk.surface}`} data-id="element-10082" data-mp="3l56b3kzxbcc2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10083" data-mp="3l2bs7xwk5zkr">
          <div className="flex items-center justify-between mb-6" data-id="element-10084" data-mp="3oop4zu9l8tq">
            <h2 className={`text-2xl font-bold flex items-center ${dk.textHeading}`} data-id="element-10085" data-mp="3v22j7plwtwg3">
              <ShoppingBagIcon className={`h-6 w-6 mr-2 ${iconAccent}`} data-id="element-10086" data-mp="37byh5mode4r7" />
              Featured Merchandise
            </h2>
            <Link to="/browse?type=merch" className={`font-medium flex items-center ${rt.accentLink}`} data-id="element-10087" data-mp="32ddpwcr3ivew">
              View all
              <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-10088" data-mp="1mmuny42v9yqo" />
            </Link>
          </div>
          <ScrollableRow data-id="element-10089" data-mp="18vpwhp9nkcrf">
            {activeFeaturedMerch.map(product => <div key={product.id} className="snap-start" data-id="element-10090" data-mp="1cwrq9qksvwm0">
                <FeaturedMerchCard product={product} data-id="element-10091" data-mp="1cx1fhby0tzqn" />
              </div>)}
          </ScrollableRow>
        </div>
      </section>

      {/* Featured Book Boxes */}
      <section className={`py-12 ${dk.page}`} data-id="element-10092" data-mp="25kdzbecsrtg9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10093" data-mp="25j2ynnenf1td">
          <div className="flex items-center justify-between mb-6" data-id="element-10094" data-mp="1rl5ykh0zkd9y">
            <h2 className={`text-2xl font-bold flex items-center ${dk.textHeading}`} data-id="element-10095" data-mp="1wjzgclnb2qfx">
              <BoxIcon className={`h-6 w-6 mr-2 ${iconAccent}`} data-id="element-10096" data-mp="g5hfw1urm0ey" />
              Featured Book Boxes
            </h2>
            <Link to="/book-boxes" className={`font-medium flex items-center ${rt.accentLink}`} data-id="element-10097" data-mp="l76o79qlrzzc">
              View all
              <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-10098" data-mp="2hv6i3e1b06eg" />
            </Link>
          </div>
          <ScrollableRow data-id="element-10099" data-mp="2d4pz7eeprskp">
            {activeBookBoxes.map(box => <div key={box.id} className="snap-start" data-id="element-10100" data-mp="2d2lniaakpp9v">
                <FeaturedBookBoxCard box={box} data-id="element-10101" data-mp="292la8sz7nk4e" />
              </div>)}
          </ScrollableRow>
        </div>
      </section>

      {/* Events Teaser */}
      <section className={`py-12 ${dk.surface}`} data-id="element-10102" data-mp="1z5gkyka2j8jd">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10103" data-mp="1z3m2dcm9rsig">
          <div className="flex items-center justify-between mb-6" data-id="element-10104" data-mp="1u5c58wgzihu7">
            <h2 className={`text-2xl font-bold flex items-center ${dk.textHeading}`} data-id="element-10105" data-mp="1q4igy1zmr10s">
              <CalendarIcon className={`h-6 w-6 mr-2 ${iconAccent}`} data-id="element-10106" data-mp="3ojv9p4l36vev" />
              Upcoming Events
            </h2>
            <Link to="/events" className={`font-medium flex items-center ${rt.accentLink}`} data-id="element-10107" data-mp="3ki9ydno7ap6s">
              View all
              <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-10108" data-mp="168xw91q6vffv" />
            </Link>
          </div>
          <ScrollableRow data-id="element-10109" data-mp="1b7r002fobs2u">
            {activeUpcomingEvents.map(event => <div key={event.id} className="snap-start" data-id="element-10110" data-mp="1g13lw4syxkrw">
                <EventTeaserCard event={event} data-id="element-10111" data-mp="1k2p0bul5i747" />
              </div>)}
          </ScrollableRow>
        </div>
      </section>

      {/* Trove Articles */}
      <section className={`py-12 ${dk.page}`} data-id="element-10112" data-mp="ne98frukjlnx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10113" data-mp="sc0y0f7qqvoy">
          <div className="flex items-center justify-between mb-6" data-id="element-10114" data-mp="wculj7h9u8hb">
            <h2 className={`text-2xl font-bold flex items-center ${dk.textHeading}`} data-id="element-10115" data-mp="wb9tboifj1mm">
              <BookOpenIcon className={`h-6 w-6 mr-2 ${iconAccent}`} data-id="element-10116" data-mp="119bag3waoqvg" />
              {isAfterTide ? 'From the Dark Trove' : 'From the Trove'}
            </h2>
            <Link to="/trove" className={`font-medium flex items-center ${rt.accentLink}`} data-id="element-10117" data-mp="2cpjeyavaec9r">
              View all articles
              <ArrowRightIcon className="h-4 w-4 ml-1" data-id="element-10118" data-mp="26b43uxdn15ua" />
            </Link>
          </div>

          {activeTroveArticles.length > 0 && <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-10119" data-mp="2kbx0porewd0k">
              {activeTroveArticles.slice(0, 3).map(article => <TroveArticleCard key={article.id} article={article} data-id="element-10120" data-mp="2gb4nkkff5nz0" />)}
            </div>}
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection data-id="element-10121" data-mp="2p8x4tu4pb5p1" />

      {/* Author/Vendor CTA */}
      <VendorCTASection data-id="element-10122" data-mp="33ymb23rnhzw6" />
    </div>;
};
export default Home;